/**
 * Kiosk 線條 SVG icon set，移植自 proto5.html 的 I(name) 函式。
 * 全部 stroke-based、吃 currentColor，所以用 color prop 控制顏色。
 * App 跑在 react-native-web（DOM 環境），直接用原生 <svg> 即可。
 */
import * as React from "react";

export type KioskIconName =
  | "cylinder"
  | "flame"
  | "shower"
  | "dryer"
  | "heater"
  | "fireplace"
  | "dots"
  | "elevator"
  | "stairs"
  | "pin"
  | "clock"
  | "phone"
  | "cash"
  | "card"
  | "mobile"
  | "building"
  | "heart"
  | "cart"
  | "trash"
  | "backspace"
  | "check"
  | "chevron"
  | "close"
  | "plus"
  | "ticket"
  | "receipt";

const PATHS: Record<KioskIconName, React.ReactNode> = {
  cylinder: (
    <>
      <path d="M7 7a5 2.5 0 0 1 10 0v10a5 2.5 0 0 1-10 0Z" />
      <path d="M7 7a5 2.5 0 0 0 10 0" />
      <path d="M10 4.7V3.2a2 .7 0 0 1 4 0v1.5" />
    </>
  ),
  flame: <path d="M12 3c1 3-2 4-2 7a2 2 0 0 0 4 0c0-1 .8-1.5 1-2 1.5 2 2 3.5 2 5a5 5 0 0 1-10 0c0-3.5 3-6 5-10Z" />,
  shower: (
    <>
      <path d="M4 12h16" />
      <path d="M6 12V7a3 3 0 0 1 6 0" />
      <path d="M9 16v.5M12 17v.5M15 16v.5M10.5 19v.5M13.5 19v.5" />
    </>
  ),
  dryer: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <circle cx="12" cy="13" r="4" />
      <path d="M8 6h.5M11 6h.5" />
    </>
  ),
  heater: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <path d="M9 5v14M14 5v14" />
    </>
  ),
  fireplace: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="1" />
      <path d="M7 20v-4h10v4" />
      <path d="M12 9c.6 1.4-1 1.8-1 3a1 1 0 0 0 2 0c.7.8 1 1.4 1 2a2 2 0 0 1-4 0c0-1.6 1.3-2.6 2-5Z" />
    </>
  ),
  dots: (
    <>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </>
  ),
  elevator: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M12 3v18" />
      <path d="M8 9l1.5-2L11 9M13 15l1.5 2 1.5-2" />
    </>
  ),
  stairs: <path d="M3 20h4v-4h4v-4h4V8h4V4" />,
  pin: (
    <>
      <path d="M12 21s7-5.5 7-11a7 7 0 0 0-14 0c0 5.5 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  phone: <path d="M5 4h3l2 5-2 1a11 11 0 0 0 5 5l1-2 5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />,
  cash: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 9v6M18 9v6" />
    </>
  ),
  card: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18M7 15h3" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="3" width="10" height="18" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  building: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="1" />
      <path d="M9 7h.5M13 7h.5M9 11h.5M13 11h.5M9 15h.5M13 15h.5M10 21v-3h4v3" />
    </>
  ),
  heart: <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10Z" />,
  receipt: (
    <>
      <path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21Z" />
      <path d="M9 8h6M9 12h6" />
    </>
  ),
  cart: (
    <>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M3 4h2l2.2 11h11l1.8-8H6" />
    </>
  ),
  trash: <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13h10l1-13" />,
  backspace: (
    <>
      <path d="M9 5h11a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9L3 12Z" />
      <path d="M12 9.5l5 5M17 9.5l-5 5" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 6.5" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  ticket: (
    <>
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z" />
      <path d="M14 6v12" strokeDasharray="2 2" />
    </>
  ),
};

interface Props {
  name: KioskIconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
}

export const KioskIcon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.8 }: Props) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}>
    {PATHS[name]}
  </svg>
);

export default KioskIcon;
